import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getStoredUserId } from '../../utils/auth';
import { API_BASE_URL } from '../../constants/apiConstants';
import './InstagramAnalytics.css';

const InstagramAnalytics = () => {
  const [accounts, setAccounts] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]); // Store all IG accounts
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [selectedFbAccount, setSelectedFbAccount] = useState(null);
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState({ caption: '', imageUrl: '' });
  const [uploading, setUploading] = useState(false);

  const parseInstagramBusinessAccount = (raw) => {
    if (!raw) return null;
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch (err) {
        return null;
      }
    }
    return raw;
  };

  const normalizeInstagramAccounts = (igAccounts = [], fbPages = []) => {
    if (Array.isArray(igAccounts) && igAccounts.length > 0) {
      return igAccounts.map((account) => ({
        ...account,
        instagram_account_id: account.instagram_account_id || account.id,
        linked_facebook_page: account.linked_facebook_page || account.facebook_page_id || account.page_id
      }));
    }

    return fbPages
      .map((page) => {
        const instagram = parseInstagramBusinessAccount(page.instagram_business_account);
        if (instagram?.id) {
          return {
            instagram_account_id: instagram.id,
            username: instagram.username,
            followers_count: instagram.followers_count,
            follows_count: instagram.follows_count,
            media_count: instagram.media_count,
            biography: instagram.biography,
            website: instagram.website,
            linked_facebook_page: page.page_id
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  const buildConnectedAccounts = (token, pages) => {
    if (!token) return [];
    return [{
      facebook_user_id: token.facebook_user_id,
      facebook_name: token.facebook_name,
      facebook_email: token.facebook_email,
      facebook_picture: token.facebook_picture,
      pages
    }];
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const userid = getStoredUserId();
      if (!userid) {
        console.log('No user ID found, skipping account fetch');
        setLoading(false);
        return;
      }

      console.log('Fetching accounts for user:', userid);

      const response = await axios.get(`${API_BASE_URL}/meta/data`, {
        headers: {
          'userid': userid
        }
      });

      console.log('Accounts API Response:', response.data);

      if (response.data.success) {
        const payload = response.data.data || {};
        const fbPages = payload.facebook_pages || [];
        const token = payload.token || null;

        const normalizedIgAccounts = normalizeInstagramAccounts(payload.instagram_accounts || [], fbPages);
        const fbAccountList = buildConnectedAccounts(token, fbPages);

        console.log('Instagram accounts found:', normalizedIgAccounts.length);
        console.log('Facebook token present:', !!token);

        setAllAccounts(normalizedIgAccounts);
        setConnectedAccounts(fbAccountList);

        if (fbAccountList.length > 0) {
          const firstFbAccount = fbAccountList[0];
          setSelectedFbAccount(firstFbAccount);

          const filteredIgAccounts = filterInstagramAccountsByFbAccount(normalizedIgAccounts, firstFbAccount);
          setAccounts(filteredIgAccounts);

          if (filteredIgAccounts.length > 0) {
            const firstAccount = filteredIgAccounts[0];
            setSelectedAccount(firstAccount);
            fetchAccountInsights(firstAccount.instagram_account_id);
            fetchInstagramMedia(firstAccount.instagram_account_id);
          } else {
            setSelectedAccount(null);
            setError('No Instagram Business accounts linked to this Facebook page');
          }
        } else if (normalizedIgAccounts.length > 0) {
          setAccounts(normalizedIgAccounts);
          const firstAccount = normalizedIgAccounts[0];
          setSelectedAccount(firstAccount);
          fetchAccountInsights(firstAccount.instagram_account_id);
          fetchInstagramMedia(firstAccount.instagram_account_id);
        } else {
          setError('No Facebook accounts found. Please authenticate first.');
        }
      }
    } catch (err) {
      console.error('Error fetching Instagram accounts:', err);
      setError('Failed to load Instagram accounts: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountInsights = async (accountId) => {
    try {
      setInsightsLoading(true);
      const userid = getStoredUserId();
      const response = await axios.get(`${API_BASE_URL}/meta/instagram/insights/${accountId}`, {
        headers: {
          'userid': userid
        }
      });

      if (response.data.success) {
        setInsights(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching insights:', err);
    } finally {
      setInsightsLoading(false);
    }
  };

  const fetchInstagramMedia = async (accountId) => {
    try {
      setPostsLoading(true);
      const userid = getStoredUserId();
      const response = await axios.get(`${API_BASE_URL}/meta/instagram/media/${accountId}`, {
        headers: {
          'userid': userid
        }
      });

      if (response.data.success) {
        setPosts(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching Instagram media:', err);
      setPosts([]);
    } finally {
      setPostsLoading(false);
    }
  };

  const handlePublishPost = async (e) => {
    e.preventDefault();
    if (!uploadData.caption || !uploadData.imageUrl) {
      alert('Please fill in all fields');
      return;
    }

    try {
      setUploading(true);
      const response = await axios.post(`${API_BASE_URL}/meta/instagram/publish`, {
        accountId: selectedAccount.instagram_account_id,
        caption: uploadData.caption,
        image_url: uploadData.imageUrl,
        media_type: 'IMAGE'
      }, {
        headers: {
          'userid': getStoredUserId()
        }
      });

      if (response.data.success) {
        alert('Post published successfully!');
        setShowUploadModal(false);
        setUploadData({ caption: '', imageUrl: '' });
        // Refresh the media list
        fetchInstagramMedia(selectedAccount.instagram_account_id);
      }
    } catch (err) {
      console.error('Error publishing post:', err);
      alert(`Failed to publish: ${err.response?.data?.message || err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleAccountSelect = (account) => {
    setSelectedAccount(account);
    fetchAccountInsights(account.instagram_account_id);
    fetchInstagramMedia(account.instagram_account_id);
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Instagram account? This will remove all connected Instagram data.')) {
      return;
    }

    try {
      setLoading(true);
      const userid = getStoredUserId();
      const response = await axios.post(`${API_BASE_URL}/meta/auth/disconnect`, {}, {
        headers: {
          'userid': userid
        }
      });

      if (response.data.success) {
        alert('Instagram account disconnected successfully');
        setAccounts([]);
        setAllAccounts([]);
        setSelectedAccount(null);
        setInsights(null);
        setConnectedAccounts([]);
        setSelectedFbAccount(null);
      }
    } catch (err) {
      console.error('Error disconnecting:', err);
      alert('Failed to disconnect account: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Filter Instagram accounts by Facebook account's pages
  const filterInstagramAccountsByFbAccount = (igAccounts, fbAccount) => {
    const pageIds = fbAccount?.pages?.map((page) => page.page_id).filter(Boolean) || [];
    if (pageIds.length === 0) return igAccounts || [];

    return (igAccounts || []).filter((igAccount) =>
      pageIds.includes(igAccount.linked_facebook_page)
    );
  };

  // Handle Facebook account change
  const handleFbAccountChange = (fbAccount) => {
    setSelectedFbAccount(fbAccount);

    // Filter Instagram accounts for this FB account
    const filteredIgAccounts = filterInstagramAccountsByFbAccount(allAccounts, fbAccount);
    setAccounts(filteredIgAccounts);

    // Auto-select first Instagram account for this FB account
    if (filteredIgAccounts.length > 0) {
      const firstAccount = filteredIgAccounts[0];
      setSelectedAccount(firstAccount);
      fetchAccountInsights(firstAccount.instagram_account_id);
      fetchInstagramMedia(firstAccount.instagram_account_id);
    } else {
      setSelectedAccount(null);
      setInsights(null);
      setPosts([]);
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  // Generate trend data from insights or current metrics
  const generateTrendData = () => {
    // If we have account insights with historical data, use it
    if (insights?.account_insights && Array.isArray(insights.account_insights)) {
      const trendData = insights.account_insights.map((item, index) => {
        const date = item.end_time ? new Date(item.end_time) : new Date(Date.now() - (6 - index) * 24 * 60 * 60 * 1000);
        const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

        return {
          date: dateStr,
          followers: item.follower_count || selectedAccount?.followers_count || 0,
          reach: item.reach || 0,
          impressions: item.impressions || 0,
          engagement: item.engagement || 0,
          fullDate: date
        };
      });

      trendData.sort((a, b) => a.fullDate - b.fullDate);
      return trendData;
    }

    // If no historical data, show message that data will populate over time
    return [];
  };

  if (loading) {
    return (
      <div className="instagram-analytics">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading Instagram accounts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="instagram-analytics">
        <div className="error-state">
          <p>❌ {error}</p>
        </div>
      </div>
    );
  }

  // Show empty state if no FB accounts at all
  if (connectedAccounts.length === 0 && accounts.length === 0 && !selectedAccount) {
    return (
      <div className="instagram-analytics">
        <div className="empty-state">
          <div className="empty-icon">📸</div>
          <h3>No Facebook Account Connected</h3>
          <p>Connect your Facebook account first to access Instagram Business analytics.</p>
        </div>
      </div>
    );
  }

  // Show empty state if selected FB account has no Instagram
  if (accounts.length === 0 && selectedFbAccount) {
    return (
      <div className="instagram-analytics">
        <div className="analytics-header">
          <div className="header-top">
            <h2>Instagram Analytics</h2>
            <div className="header-actions">
              {connectedAccounts.length > 1 ? (
                <div className="account-switcher">
                  <label className="account-switcher-label">Connected Account:</label>
                  <select
                    className="account-select"
                    value={selectedFbAccount?.facebook_user_id || ''}
                    onChange={(e) => {
                      const account = connectedAccounts.find(acc => acc.facebook_user_id === e.target.value);
                      handleFbAccountChange(account);
                    }}
                  >
                    {connectedAccounts.map((account) => (
                      <option key={account.facebook_user_id} value={account.facebook_user_id}>
                        {account.facebook_name} ({account.facebook_email})
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="connected-account-badge">
                  <img
                    src={selectedFbAccount?.facebook_picture}
                    alt={selectedFbAccount?.facebook_name}
                    className="connected-avatar"
                  />
                  <div className="connected-info">
                    <span className="connected-label">Connected as</span>
                    <span className="connected-name">{selectedFbAccount?.facebook_name}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="empty-state">
          <div className="empty-icon">📸</div>
          <h3>No Instagram Account for {selectedFbAccount.facebook_name}</h3>
          <p>This Facebook account doesn't have any pages with linked Instagram Business accounts.</p>
          <div className="empty-note">
            <strong>To connect Instagram:</strong>
            <ul>
              <li>Make sure you have a Facebook Page</li>
              <li>Convert your Instagram to a Business or Creator account</li>
              <li>Link your Instagram Business account to your Facebook Page</li>
              <li>Reconnect your Facebook account in our app</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="instagram-analytics">
      <div className="analytics-header">
        <div className="header-top">
          <h2>Instagram Analytics</h2>
          <div className="header-actions">
            {connectedAccounts.length > 0 && (
              <>
                {connectedAccounts.length > 1 ? (
                  <div className="account-switcher">
                    <label className="account-switcher-label">Connected Account:</label>
                    <select
                      className="account-select"
                      value={selectedFbAccount?.facebook_user_id || ''}
                      onChange={(e) => {
                        const account = connectedAccounts.find(acc => acc.facebook_user_id === e.target.value);
                        handleFbAccountChange(account);
                      }}
                    >
                      {connectedAccounts.map((account) => (
                        <option key={account.facebook_user_id} value={account.facebook_user_id}>
                          {account.facebook_name} ({account.facebook_email})
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="connected-account-badge">
                    <img
                      src={selectedFbAccount?.facebook_picture}
                      alt={selectedFbAccount?.facebook_name}
                      className="connected-avatar"
                    />
                    <div className="connected-info">
                      <span className="connected-label">Connected as</span>
                      <span className="connected-name">{selectedFbAccount?.facebook_name}</span>
                    </div>
                  </div>
                )}
                <button
                  className="action-btn disconnect-btn"
                  onClick={handleDisconnect}
                  title="Disconnect Instagram account"
                >
                  ✕ Disconnect
                </button>
              </>
            )}
          </div>
        </div>
        <div className="account-selector">
          {accounts.map((account) => (
            <button
              key={account.instagram_account_id}
              className={`account-tab ${selectedAccount?.instagram_account_id === account.instagram_account_id ? 'active' : ''}`}
              onClick={() => handleAccountSelect(account)}
            >
              @{account.username}
            </button>
          ))}
        </div>
      </div>

      {selectedAccount && (
        <div className="analytics-content">
          {/* Account Overview */}
          <div className="account-overview">
            <div className="account-header">
              {selectedAccount.profile_picture_url && (
                <img src={selectedAccount.profile_picture_url} alt={selectedAccount.username} className="account-avatar" />
              )}
              <div className="account-info">
                <h3>@{selectedAccount.username}</h3>
                <p className="account-type">{selectedAccount.account_type || 'BUSINESS'}</p>
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          {insightsLoading ? (
            <div className="insights-loading">
              <div className="spinner-small"></div>
              <p>Loading insights...</p>
            </div>
          ) : (
            <>
              <div className="metrics-grid">
                <div className="metric-card">
                  <div className="metric-icon">👥</div>
                  <div className="metric-content">
                    <p className="metric-label">Followers</p>
                    <h3 className="metric-value">{formatNumber(selectedAccount.followers_count || 0)}</h3>
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-icon">📸</div>
                  <div className="metric-content">
                    <p className="metric-label">Posts</p>
                    <h3 className="metric-value">{formatNumber(selectedAccount.media_count || 0)}</h3>
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-icon">➡️</div>
                  <div className="metric-content">
                    <p className="metric-label">Following</p>
                    <h3 className="metric-value">{formatNumber(selectedAccount.follows_count || 0)}</h3>
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-icon">📊</div>
                  <div className="metric-content">
                    <p className="metric-label">Engagement Rate</p>
                    <h3 className="metric-value">
                      {selectedAccount.followers_count > 0
                        ? ((selectedAccount.follows_count / selectedAccount.followers_count) * 100).toFixed(1) + '%'
                        : '0%'}
                    </h3>
                  </div>
                </div>
              </div>

              {/* Account Details */}
              <div className="account-details">
                <h4>Account Information</h4>
                <div className="details-grid">
                  {selectedAccount.biography && (
                    <div className="detail-item">
                      <strong>Bio:</strong>
                      <p>{selectedAccount.biography}</p>
                    </div>
                  )}
                  {selectedAccount.website && (
                    <div className="detail-item">
                      <strong>Website:</strong>
                      <a href={selectedAccount.website} target="_blank" rel="noopener noreferrer">
                        {selectedAccount.website}
                      </a>
                    </div>
                  )}
                  <div className="detail-item">
                    <strong>Account Type:</strong>
                    <p>{selectedAccount.account_type || 'BUSINESS'}</p>
                  </div>
                  {selectedAccount.linked_facebook_page && (
                    <div className="detail-item">
                      <strong>Linked Facebook Page:</strong>
                      <p>{selectedAccount.linked_facebook_page}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Performance Trends */}
              <div className="trends-section">
                <h4>Performance Trends</h4>
                <p className="trends-subtitle">
                  {generateTrendData().length > 0
                    ? `Showing ${generateTrendData().length} day${generateTrendData().length !== 1 ? 's' : ''} of historical data`
                    : 'Historical trend data will appear here once insights are available. Connect your Instagram Business account and check back after some activity.'}
                </p>

                {generateTrendData().length > 0 ? (
                  <div className="charts-grid">
                    {/* Follower Growth Chart */}
                    <div className="chart-card">
                      <h5>Follower Growth</h5>
                      <ResponsiveContainer width="100%" height={250}>
                        <AreaChart data={generateTrendData()}>
                          <defs>
                            <linearGradient id="colorFollowers" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#E1306C" stopOpacity={0.8} />
                              <stop offset="95%" stopColor="#E1306C" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: '12px' }} />
                          <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                            }}
                          />
                          <Area type="monotone" dataKey="followers" stroke="#E1306C" fillOpacity={1} fill="url(#colorFollowers)" name="Followers" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Reach Chart */}
                    <div className="chart-card">
                      <h5>Account Reach</h5>
                      <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={generateTrendData()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: '12px' }} />
                          <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                            }}
                          />
                          <Line type="monotone" dataKey="reach" stroke="#833AB4" strokeWidth={2} dot={{ fill: '#833AB4', r: 4 }} name="Reach" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Engagement Chart */}
                    <div className="chart-card">
                      <h5>Daily Engagement</h5>
                      <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={generateTrendData()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: '12px' }} />
                          <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                            }}
                          />
                          <Line type="monotone" dataKey="engagement" stroke="#C13584" strokeWidth={2} dot={{ fill: '#C13584', r: 4 }} name="Engagements" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="no-trends-placeholder">
                    <div className="placeholder-icon">📊</div>
                    <p>Performance charts will appear here once Instagram insights data becomes available</p>
                  </div>
                )}
              </div>

              {/* Posts and Content */}
              <div className="posts-section">
                <div className="posts-header">
                  <h4>Recent Posts</h4>
                  <button
                    className="action-btn upload-btn"
                    onClick={() => setShowUploadModal(true)}
                    title="Upload new post"
                  >
                    📤 Upload Post
                  </button>
                </div>

                {postsLoading ? (
                  <div className="posts-loading">
                    <div className="spinner-small"></div>
                    <p>Loading posts...</p>
                  </div>
                ) : posts.length > 0 ? (
                  <div className="posts-grid">
                    {posts.map((post) => (
                      <div key={post.id} className="post-card">
                        <div className="post-image-wrapper">
                          {post.media_url && (
                            <img src={post.media_url} alt="Post" className="post-image" />
                          )}
                          {post.thumbnail_url && !post.media_url && (
                            <img src={post.thumbnail_url} alt="Post thumbnail" className="post-image" />
                          )}
                        </div>
                        <div className="post-content">
                          {post.caption && (
                            <p className="post-caption">{post.caption.substring(0, 100)}...</p>
                          )}
                          <div className="post-metrics">
                            {post.like_count !== undefined && (
                              <div className="metric">
                                <span className="metric-icon">❤️</span>
                                <span className="metric-value">{formatNumber(post.like_count)}</span>
                              </div>
                            )}
                            {post.comments_count !== undefined && (
                              <div className="metric">
                                <span className="metric-icon">💬</span>
                                <span className="metric-value">{formatNumber(post.comments_count)}</span>
                              </div>
                            )}
                            {post.timestamp && (
                              <div className="metric">
                                <span className="metric-icon">📅</span>
                                <span className="metric-value">
                                  {new Date(post.timestamp).toLocaleDateString()}
                                </span>
                              </div>
                            )}
                          </div>
                          {Array.isArray(post.insights) && post.insights.length > 0 && (
                            <div className="post-metrics">
                              {post.insights.map((metric) => {
                                const metricValue = Array.isArray(metric.values) && metric.values.length > 0
                                  ? metric.values[0]?.value
                                  : metric.value;
                                return (
                                  <div key={metric.id || metric.name} className="metric">
                                    <span className="metric-icon">📈</span>
                                    <span className="metric-value">{formatNumber(metricValue || 0)}</span>
                                    <span className="metric-label">{metric.title || metric.name}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {post.permalink && (
                            <a href={post.permalink} target="_blank" rel="noopener noreferrer" className="post-link">
                              View on Instagram →
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-posts-placeholder">
                    <div className="placeholder-icon">📸</div>
                    <p>No posts found. Start by uploading a post!</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Upload Post to Instagram</h3>
              <button
                className="modal-close"
                onClick={() => setShowUploadModal(false)}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handlePublishPost}>
              <div className="form-group">
                <label>Image URL</label>
                <input
                  type="url"
                  value={uploadData.imageUrl}
                  onChange={(e) => setUploadData({ ...uploadData, imageUrl: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  required
                />
                {uploadData.imageUrl && (
                  <div className="image-preview">
                    <img src={uploadData.imageUrl} alt="Preview" />
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Caption</label>
                <textarea
                  value={uploadData.caption}
                  onChange={(e) => setUploadData({ ...uploadData, caption: e.target.value })}
                  placeholder="Write your caption here..."
                  rows="4"
                  required
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowUploadModal(false)}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={uploading}
                >
                  {uploading ? 'Publishing...' : 'Publish Post'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstagramAnalytics;
